import useAPI from "@toothfairy/shared-api/useApi";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import { Popable } from "react-native-popable";
import envConfig from "../../../envConfig";
import Files from "../../API/Files";
import useWorkspaces from "../../Hooks/useWorkspaces";
import LoadingPlaceholder from "../LoadingPlaceholder";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "avif"];
const AUDIO_EXTENSIONS = ["mp3", "m4a", "aac", "flac", "ogg"];
const VIDEO_EXTENSIONS = ["mp4", "webm", "mov"];

const ArtifactViewer = ({ path, documentId, onDownload = () => {} }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentTheme } = AppTheme.useTheme();
  const { getActiveWorkspace } = useWorkspaces();
  const { apiRequest: S3downloadUrlRequest } = useAPI(
    Files.downloadUrlGeneration,
    envConfig,
  );

  const fileExt = path?.split(".")?.pop()?.toLowerCase();
  const isImage = IMAGE_EXTENSIONS.includes(fileExt);
  const isAudio = AUDIO_EXTENSIONS.includes(fileExt);
  const isVideo = VIDEO_EXTENSIONS.includes(fileExt);

  const getUrl = async () => {
    setLoading(true);
    try {
      const _url = await S3downloadUrlRequest({
        workspaceid: getActiveWorkspace()?.id,
        logId: null,
        type: fileExt,
        filename: path.replace(
          `s3://files-to-import-${envConfig.REACT_APP_REGION}-${envConfig.REACT_APP_ENV}/`,
          "",
        ),
        context: "designArtifact",
      });
      setUrl(_url?.url);
    } catch (error) {
      console.error("Error fetching artifact URL:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!path) return;
    getUrl();
  }, [path]);

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <LoadingPlaceholder
          style={{ padding: 10 }}
          visible
          caption={t("mrcdocs.ui.loadingDocument")}
        />
      </View>
    );
  }

  if (!url) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <AppPlaceholderView
          wrapperStyle={{ width: 300, height: 200 }}
          size={30}
          icon={AppAssets.icons.orbit}
          mainCaption={t("mrcdocs.ui.documentDoesNotExistCaption")}
          subCaption={t("mrcdocs.ui.documentDoesNotExistSubCaption")}
        />
      </View>
    );
  }

  return (
    <View style={{ flex: 1, padding: 10 }}>
      <View
        style={{
          flex: 1,
          borderRadius: 10,
          overflow: "hidden",
          backgroundColor: currentTheme?.theme?.softBackgroundColor,
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
        }}
      >
        {isImage && (
          <img
            src={url}
            alt="artifact"
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
            }}
          />
        )}
        {isAudio && (
          <audio src={url} controls style={{ width: "100%", maxWidth: 600 }} />
        )}
        {isVideo && (
          <video
            src={url}
            controls
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
            }}
          />
        )}
        {!isImage && !isAudio && !isVideo && (
          <View style={{ alignItems: "center", padding: 20 }}>
            <AppText color={currentTheme?.theme?.font_color}>
              {t("mrcdocs.ui.unsupportedArtifactType")}
            </AppText>
            <AppText
              preventTranslation
              style={{ marginTop: 5, fontSize: 12 }}
              color={currentTheme?.theme?.darkGrey}
            >
              {fileExt}
            </AppText>
          </View>
        )}
      </View>
    </View>
  );
};

export default ArtifactViewer;
